<template>
  <div
    :class="[
      'chat-list-item',
      {
        unread: isLastMessageUnread(),
        general: isGeneralChat,
      },
    ]"
  >
    <ChatListAvatars
      v-if="!isGeneralChat"
      :conversation="conversation"
      :participants="chatParticipants"
    />
    <div class="participants-first-message-wrapper">
      <div v-if="isGeneralChat" class="general-chat">
        <FriedH5>{{ t('dsr_chat_all_participants') }}</FriedH5>
      </div>
      <FriedH4
        v-else
        v-tooltip="{ content: getParticipantNames, offset: 6, delay: { show: 300, hide: 100 } }"
        class="participants-text"
      >
        {{ getParticipantNames }}
      </FriedH4>
      <ChatTypingStatus
        v-if="isTypingInConversation(conversation.id, typing)"
        :typing="typing"
        :owner="owner"
        :conversation="conversation"
        compact-view
      />
      <FriedParagraph v-else-if="conversationDraft" class="first-message">
        <span class="draft-text">{{ `${t('draft')}:` }}</span>
        {{ conversationDraft }}</FriedParagraph
      >
      <FriedParagraph v-else-if="conversation.messages" class="first-message">{{
        getLatestMessage(conversation.messages)
      }}</FriedParagraph>
    </div>
    <div class="unread-circle" />
  </div>
</template>
<script lang="ts">
import type { PropType } from 'vue';
import { computed, defineComponent } from 'vue';
import { useI18n } from 'vue-i18n';
import { AvatarSize } from '@getaccept/fried-tofu';
import type { User } from '@getaccept/lib-shared-new/src/users/types/user';
import { useSessionStorage } from '@vueuse/core';
import type { ChatParticipant, Conversation, Message, MessageDraft, Typing } from '../types';
import { ConversationType } from '../types';
import { ChatHelper } from '../helpers';
import type { Participant } from '../../types';
import ChatTypingStatus from './ChatTypingStatus.vue';
import ChatListAvatars from './ChatListAvatars.vue';

export default defineComponent({
  components: {
    ChatTypingStatus,
    ChatListAvatars,
  },
  props: {
    conversation: {
      type: Object as PropType<Conversation>,
      default: () => ({}),
    },
    chatParticipants: {
      type: Array as PropType<ChatParticipant[]>,
      default: () => [],
    },
    user: {
      type: Object as PropType<Participant | User>,
      default: () => ({} as Participant),
    },
    participantId: {
      type: String,
      default: '',
    },
    typing: {
      type: Array as PropType<Array<Typing>>,
      default: () => [],
    },
    participants: {
      type: Array as PropType<Array<Participant>>,
      default: () => [],
    },
    publishedView: {
      type: Boolean,
    },
    draftStorageKey: {
      type: String,
      required: true,
    },
    previousRoomOwnersIds: {
      type: Array as PropType<string[]>,
      default: () => [],
    },
    previousRoomOwners: {
      type: Array as PropType<User[]>,
      default: () => [],
    },
  },
  setup(props) {
    const { t } = useI18n();
    const draftMessages = useSessionStorage<MessageDraft[]>(props.draftStorageKey, []);

    const owner = computed(() =>
      props.participantId
        ? { participantId: props.participantId }
        : { user: props.user, userId: props.user.id }
    );

    const conversationDraft = computed(() =>
      ChatHelper.getDraftMessage(draftMessages.value, props.conversation.id)
    );

    const isTypingInConversation = (conversationId: string, typing: Typing[]) =>
      typing.some(
        t =>
          t.conversationId === conversationId &&
          !ChatHelper.isSameParticipant(t.participant, owner.value)
      );

    const currentUser = computed(() =>
      props.publishedView
        ? { participantId: props.participantId }
        : {
            userId: props.user.id,
          }
    );

    const getParticipantNames = computed(() =>
      ChatHelper.getParticipantNames({
        participants: props.chatParticipants,
        conversationParticipants: props.conversation.participants,
        user: props.user,
        currentParticipantId: currentUser.value?.participantId,
        currentUserId: currentUser.value?.userId,
        previousRoomOwnersIds: props.previousRoomOwnersIds,
        previousRoomOwners: props.previousRoomOwners,
      })
    );

    const getLatestMessage = (messages: Message[]) => {
      const filteredMessages = messages.filter(message => !message.deletedAt);
      const latestMessage = ChatHelper.getLastMessage(filteredMessages);
      const sender = ChatHelper.getParticipant(latestMessage, props.participants);
      let firstName = '';

      if (sender && sender.id === currentUser.value?.participantId) {
        firstName = t('you');
      }

      if (sender && sender.id !== currentUser.value?.participantId) {
        firstName = sender.firstName || sender.email;
      }

      if (!sender && latestMessage?.owner?.userId === currentUser.value?.userId) {
        firstName = t('you');
      }

      if (!sender && latestMessage?.owner?.userId === props.user.id) {
        firstName = props.user.firstName || props.user.email;
      }

      if (!sender && props.previousRoomOwnersIds.includes(latestMessage?.owner?.userId)) {
        const previousOwnerData = props.previousRoomOwners.find(
          owner => owner.id === latestMessage?.owner?.userId
        );

        if (previousOwnerData) {
          firstName = previousOwnerData.firstName || previousOwnerData.email;
        } else {
          firstName = t('dr_chat_previous_owner');
        }
      }

      if (latestMessage?.deletedAt) {
        return `${firstName} ${t('deleted_a_message')}`;
      }

      return latestMessage?.text ? `${firstName}: ${latestMessage?.text}` : '';
    };

    const isLastMessageUnread = () =>
      ChatHelper.isLastMessageUnread(props.conversation, props.user.id, props.participantId);

    const isGeneralChat = computed(() => props.conversation.type === ConversationType.General);

    return {
      owner,
      getParticipantNames,
      isTypingInConversation,
      getLatestMessage,
      isLastMessageUnread,
      isGeneralChat,
      t,

      getParticipantsDetails: ChatHelper.getParticipantsDetails,
      ConversationType,
      AvatarSize,
      conversationDraft,
    };
  },
});
</script>
<style lang="scss" scoped>
@import '@getaccept/dsr-shared-new/src/scss/chat-view';
@import '@getaccept/dsr-shared-new/src/scss/chat-list-item';
</style>
